// Pure functions over raw InnerTube JSON (/player and /next). No I/O.

import type { Chapter, VideoMeta } from '../api';
import { YouTubeError } from './errors';

/* eslint-disable @typescript-eslint/no-explicit-any -- InnerTube JSON is untyped and changes often */
type Json = any;

export interface CaptionTrack {
  baseUrl: string;
  languageCode: string;
  /** "asr" for auto-generated tracks. */
  kind?: string;
  name?: string;
  vssId?: string;
}

// ---------------------------------------------------------------------------------------------
// Playability

export type Playability =
  | { ok: true }
  | { ok: false; error: YouTubeError; /** True if another client/IP cannot change the outcome. */ final: boolean };

export function classifyPlayability(player: Json): Playability {
  const ps = player?.playabilityStatus;
  const status: string | undefined = ps?.status;
  const reason = [ps?.reason, textOf(ps?.errorScreen?.playerErrorMessageRenderer?.subreason), ps?.messages?.join?.(' ')]
    .filter(Boolean)
    .join(' ')
    .trim();
  if (status === 'OK') return { ok: true };
  if (/\bbot\b/i.test(reason)) {
    return { ok: false, final: false, error: new YouTubeError('bot_check', `YouTube blocked the request: ${reason}`) };
  }
  if (/members|join this channel/i.test(reason)) {
    return { ok: false, final: true, error: new YouTubeError('login_required', `Members-only video: ${reason}`) };
  }
  if (/country|region/i.test(reason)) {
    // Geo-blocked for this IP; client mode from another country might work.
    return { ok: false, final: true, error: new YouTubeError('not_found', `Video not available in this region: ${reason}`) };
  }
  if (status === 'ERROR' || /unavailable|private|removed|terminated|does not exist/i.test(reason)) {
    // Note: WEB/MWEB/TV without PO token answer "Video unavailable"/"page needs to be reloaded" for
    // existing videos, which is why only native clients are used for /player.
    return { ok: false, final: true, error: new YouTubeError('not_found', `Video not available: ${reason || status}`) };
  }
  if (status === 'LIVE_STREAM_OFFLINE' || player?.videoDetails?.isUpcoming) {
    return { ok: false, final: true, error: new YouTubeError('live', `Live stream has not ended yet: ${reason || status}`) };
  }
  if (status === 'LOGIN_REQUIRED' || status === 'AGE_CHECK_REQUIRED' || status === 'AGE_VERIFICATION_REQUIRED' || status === 'CONTENT_CHECK_REQUIRED') {
    return { ok: false, final: false, error: new YouTubeError('login_required', `YouTube requires sign-in: ${reason || status}`) };
  }
  return {
    ok: false,
    final: false,
    error: new YouTubeError('bad_response', `Unexpected playability status ${status ?? '(missing)'}${reason ? `: ${reason}` : ''}`),
  };
}

// ---------------------------------------------------------------------------------------------
// Caption tracks

export function captionTracks(player: Json): CaptionTrack[] {
  const tracks: Json[] = player?.captions?.playerCaptionsTracklistRenderer?.captionTracks ?? [];
  return tracks
    .filter((t) => typeof t?.baseUrl === 'string' && typeof t?.languageCode === 'string')
    .map((t) => ({
      baseUrl: t.baseUrl,
      languageCode: t.languageCode,
      kind: t.kind || undefined,
      name: textOf(t.name),
      vssId: t.vssId,
    }));
}

const baseLang = (code: string) => code.toLowerCase().split(/[-_]/)[0] ?? code;

export interface AudioTrack {
  /** "de-DE.4": language + kind (".4" original, ".3" dubbed, ".10" auto-dubbed). */
  id: string;
  original: boolean;
  dubbed: boolean;
  isDefault: boolean;
}

/**
 * Audio tracks of a video with several (dubbed videos), from the stream formats and the caption
 * renderer. The default track follows the request's hl, so for hl=en an English auto-dub is the
 * default of a German video; only the "original" markers say which language is spoken.
 */
export function audioTracks(player: Json): AudioTrack[] {
  const byId = new Map<string, AudioTrack>();
  const add = (id: unknown, info: { xtags?: string; displayName?: string; isAutoDubbed?: boolean; isDefault?: boolean }) => {
    if (typeof id !== 'string' || !id) return;
    const xtags = decodeXtags(info.xtags);
    const kind = /\.(\d+)$/.exec(id)?.[1];
    const track = byId.get(id) ?? { id, original: false, dubbed: false, isDefault: false };
    track.original ||= kind === '4' || /acont\W{0,2}original/.test(xtags) || /\boriginal\b/i.test(info.displayName ?? '');
    track.dubbed ||= kind === '3' || kind === '10' || !!info.isAutoDubbed || /acont\W{0,2}dubbed/.test(xtags);
    track.isDefault ||= !!info.isDefault;
    byId.set(id, track);
  };
  for (const f of (player?.streamingData?.adaptiveFormats ?? []) as Json[]) {
    const a = f?.audioTrack;
    if (!a) continue;
    const xtags = typeof f.xtags === 'string' ? f.xtags : typeof f.url === 'string' ? searchParam(f.url, 'xtags') : undefined;
    add(a.id, { xtags, displayName: a.displayName, isAutoDubbed: a.isAutoDubbed, isDefault: a.audioIsDefault });
  }
  for (const a of (player?.captions?.playerCaptionsTracklistRenderer?.audioTracks ?? []) as Json[]) add(a?.audioTrackId, {});
  return [...byId.values()];
}

/**
 * Best guess of the spoken (original) language, as base language code ("en", "de").
 * Auto-dubbed videos list ASR tracks for the dub languages too, so "the ASR track" is not enough.
 */
export function originalLanguage(player: Json, tracks: CaptionTrack[] = captionTracks(player)): string | undefined {
  const renderer = player?.captions?.playerCaptionsTracklistRenderer;
  const audio = audioTracks(player);
  const audioLang = (track: AudioTrack) => baseLang(track.id.split('.')[0] ?? track.id);
  const dubbed = new Set(audio.filter((a) => a.dubbed && !a.original).map(audioLang));
  const notDubbed = (track: CaptionTrack | undefined) => (track && !dubbed.has(baseLang(track.languageCode)) ? track : undefined);

  // The audio track marked as original (dubbed videos).
  const originalAudio = audio.find((a) => a.original);
  if (originalAudio) return audioLang(originalAudio);

  const asr = tracks.filter((t) => t.kind === 'asr');
  if (asr.length === 1 && notDubbed(asr[0])) return baseLang(asr[0]!.languageCode);

  // The track YouTube auto-translates from ("defaultTranslationSourceTrackIndices"); follows the
  // default audio track, which may be a dub.
  const sourceIndex = renderer?.defaultTranslationSourceTrackIndices?.[0];
  const sourceTrack = notDubbed(typeof sourceIndex === 'number' ? tracks[sourceIndex] : undefined);
  if (sourceTrack) return baseLang(sourceTrack.languageCode);

  const defaultAudio = audio.find((a) => a.isDefault && !a.dubbed);
  if (defaultAudio) return audioLang(defaultAudio);

  const audioTrack = renderer?.audioTracks?.[renderer?.defaultAudioTrackIndex ?? 0];
  const defaultTrack = notDubbed(tracks[audioTrack?.defaultCaptionTrackIndex ?? -1]);
  if (defaultTrack) return baseLang(defaultTrack.languageCode);
  return undefined;
}

/** xtags of a stream format: "acont=original:lang=de-DE" (IOS) or the same as base64url protobuf (ANDROID). */
function decodeXtags(xtags: string | undefined): string {
  if (!xtags) return '';
  if (xtags.includes('=') && !/^[\w-]+=*$/.test(xtags)) return xtags;
  try {
    return atob(xtags.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(xtags.length / 4) * 4, '='));
  } catch {
    return xtags;
  }
}

function searchParam(url: string, name: string): string | undefined {
  try {
    return new URL(url).searchParams.get(name) ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * Picks the transcript track closest to the video: original language before any other
 * (translations, dubs), within a language the creator's track before the auto-generated one.
 * Without a track in the original language: `preferredLang`, then any manual track, then anything.
 */
export function selectCaptionTrack(player: Json, preferredLang?: string): CaptionTrack | undefined {
  const tracks = captionTracks(player);
  if (tracks.length === 0) return undefined;
  const inLang = (lang: string | undefined) => (lang ? tracks.filter((t) => baseLang(t.languageCode) === baseLang(lang)) : []);
  const best = (list: CaptionTrack[]) => list.find((t) => t.kind !== 'asr') ?? list[0];
  return (
    best(inLang(originalLanguage(player, tracks))) ??
    best(inLang(preferredLang)) ??
    tracks.find((t) => t.kind !== 'asr') ??
    tracks[0]
  );
}

// ---------------------------------------------------------------------------------------------
// Metadata

export interface ExtractedMeta extends Omit<VideoMeta, 'videoId' | 'title' | 'channel'> {
  title?: string;
  channel?: string;
  description?: string;
  chapters?: Chapter[];
}

/** Metadata from a native-client /player response (videoDetails). */
export function metaFromPlayer(player: Json): ExtractedMeta {
  const d = player?.videoDetails;
  if (!d) return {};
  const length = Number(d.lengthSeconds);
  const thumbs: Json[] = d.thumbnail?.thumbnails ?? [];
  const largest = [...thumbs].sort((a, b) => (b?.width ?? 0) - (a?.width ?? 0))[0];
  return {
    title: d.title || undefined,
    channel: d.author || undefined,
    channelUrl: d.channelId ? `https://www.youtube.com/channel/${d.channelId}` : undefined,
    durationSeconds: Number.isFinite(length) && length > 0 ? length : undefined,
    thumbnailUrl: typeof largest?.url === 'string' ? largest.url.split('?')[0] : undefined,
    description: typeof d.shortDescription === 'string' ? d.shortDescription : undefined,
  };
}

/** Metadata from a WEB /next response. Returns null if the video does not exist. */
export function metaFromNext(next: Json): ExtractedMeta | null {
  const contents: Json[] = next?.contents?.twoColumnWatchNextResults?.results?.results?.contents ?? [];
  const primary = contents.find((c) => c?.videoPrimaryInfoRenderer)?.videoPrimaryInfoRenderer;
  const secondary = contents.find((c) => c?.videoSecondaryInfoRenderer)?.videoSecondaryInfoRenderer;
  if (!primary && !secondary) return null;
  const owner = secondary?.owner?.videoOwnerRenderer;
  const ownerPath: string | undefined =
    owner?.navigationEndpoint?.browseEndpoint?.canonicalBaseUrl ??
    owner?.title?.runs?.[0]?.navigationEndpoint?.browseEndpoint?.canonicalBaseUrl;
  const dateText = textOf(primary?.dateText);
  return {
    title: textOf(primary?.title),
    channel: textOf(owner?.title),
    channelUrl: ownerPath ? `https://www.youtube.com${ownerPath}` : undefined,
    publishedAt: dateText ? toIsoDate(dateText) : undefined,
    description: secondary?.attributedDescription?.content ?? textOf(secondary?.description),
    chapters: chaptersFromNext(next),
  };
}

/** True if /next renders the "This video isn't available anymore" placeholder instead of a video. */
export function nextSaysUnavailable(next: Json): boolean {
  if (metaFromNext(next)) return false;
  const contents: Json[] = next?.contents?.twoColumnWatchNextResults?.results?.results?.contents ?? [];
  return contents.some((c) => (c?.itemSectionRenderer?.contents ?? []).some((i: Json) => i?.backgroundPromoRenderer));
}

export function visitorDataFromResponse(response: Json): string | undefined {
  const value = response?.responseContext?.visitorData;
  return typeof value === 'string' && value ? value : undefined;
}

/** Creator chapters (DESCRIPTION_CHAPTERS), else YouTube's auto chapters. */
export function chaptersFromNext(next: Json): Chapter[] | undefined {
  const markers: Json[] =
    next?.playerOverlays?.playerOverlayRenderer?.decoratedPlayerBarRenderer?.decoratedPlayerBarRenderer?.playerBar
      ?.multiMarkersPlayerBarRenderer?.markersMap ?? [];
  const byKey = (key: string) => markers.find((m) => m?.key === key)?.value?.chapters as Json[] | undefined;
  const raw = byKey('DESCRIPTION_CHAPTERS') ?? byKey('AUTO_CHAPTERS') ?? markers.find((m) => m?.value?.chapters)?.value?.chapters;
  const chapters = (raw ?? [])
    .map((c: Json) => c?.chapterRenderer)
    .filter(Boolean)
    .map((c: Json) => ({ title: textOf(c.title) ?? '', start: Math.round(Number(c.timeRangeStartMillis ?? 0)) / 1000 }))
    .filter((c: Chapter) => c.title && Number.isFinite(c.start));
  return chapters.length ? chapters : undefined;
}

/** Chapters from "0:00 Intro" lines in a description (fallback when /next is unavailable). */
export function chaptersFromDescription(description: string | undefined): Chapter[] | undefined {
  if (!description) return undefined;
  const chapters: Chapter[] = [];
  for (const line of description.split('\n')) {
    const m = /^\s*[([]?((?:\d{1,2}:)?\d{1,2}:\d{2})[)\]]?\s*[-–—:|]?\s*(.+?)\s*$/.exec(line);
    if (!m?.[1] || !m[2]) continue;
    const start = m[1].split(':').reduce((acc, part) => acc * 60 + Number(part), 0);
    chapters.push({ start, title: m[2] });
  }
  // YouTube's own rule: first chapter at 0:00, at least 3 chapters, ascending.
  const valid = chapters.length >= 3 && chapters[0]?.start === 0 && chapters.every((c, i) => i === 0 || c.start > (chapters[i - 1]?.start ?? 0));
  return valid ? chapters : undefined;
}

// ---------------------------------------------------------------------------------------------
// Helpers

/** Text of an InnerTube text object ({simpleText} | {runs:[{text}]} | {content}) or plain string. */
export function textOf(value: Json): string | undefined {
  if (value == null) return undefined;
  if (typeof value === 'string') return value || undefined;
  if (typeof value.simpleText === 'string') return value.simpleText || undefined;
  if (Array.isArray(value.runs)) return value.runs.map((r: Json) => r?.text ?? '').join('') || undefined;
  if (typeof value.content === 'string') return value.content || undefined;
  return undefined;
}

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/** "Jun 17, 2024" / "Premiered Jun 17, 2024" / "Streamed live on Jun 17, 2024" -> "2024-06-17"; otherwise unchanged. */
export function toIsoDate(text: string): string {
  const m = /([A-Z][a-z]{2})[a-z]*\.? (\d{1,2}), (\d{4})/.exec(text);
  const month = m?.[1] ? MONTHS.indexOf(m[1].toLowerCase()) : -1;
  if (!m || month < 0) return text;
  return `${m[3]}-${String(month + 1).padStart(2, '0')}-${String(Number(m[2])).padStart(2, '0')}`;
}
